import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Facebook.css";
import debounce from "lodash.debounce";

function Facebook() {
  const [token, setToken] = useState(localStorage.getItem("fbToken") || ""); 
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState("");
  const [leads, setLeads] = useState([]);
  const [name, setname] = useState("");

  useEffect(() => {
    const checkExistingConnection = async () => {
      try {
        const Authtoken = localStorage.getItem("token"); 
        if (!Authtoken) return;

        const response = await axios.get("https://leadbankapi.gensparc.com/api/facebook/user", {
          headers: { Authorization: `Bearer ${Authtoken}` },
        });

        if (response.data.token) {
          setToken(response.data.token);
          localStorage.setItem("fbToken", response.data.token); 

          setSelectedPage(response.data.pageId || "");
          setSelectedForm(response.data.formId || "");
          setname(response.data.name || ""); // Load campaign name

          fetchPages(response.data.token);
          if (response.data.pageId) {
            fetchForms(response.data.pageId, response.data.token);
          }
        }
      } catch (error) {
        console.error("Error checking existing connection:", error);
      }
    };

    if (!token) {
      checkExistingConnection();
    } else {
      fetchPages(token);
    }
  }, [token]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin === "https://leadbankapi.gensparc.com" && event.data.accessToken) {
        setToken(event.data.accessToken);
        localStorage.setItem("fbToken", event.data.accessToken);
        saveUserToken(event.data.accessToken);
        fetchPages(event.data.accessToken);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const saveUserToken = async (accessToken) => {
    try {
      const Authtoken = localStorage.getItem("token");

      await axios.post(
        "https://leadbankapi.gensparc.com/api/facebook/save-token",
        { token: accessToken },
        { headers: { Authorization: `Bearer ${Authtoken}` } }
      );
    } catch (error) {
      console.error("Error saving token:", error);
    }
  };

  const openFacebookLoginPopup = () => {
    const popup = window.open(
      "https://leadbankapi.gensparc.com/api/facebook/auth",
      "FacebookLogin",
      "width=600,height=600"
    );

    if (!popup) {
      alert("Popup blocked! Please allow popups and try again.");
    }
  };

  const fetchPages = async (token) => {
    try {
      const Authtoken = localStorage.getItem("token");
      const response = await axios.get(`https://leadbankapi.gensparc.com/api/facebook/pages`, {
        headers: { Authorization: `Bearer ${Authtoken}` },
        params: { token },
      });
      setPages(response.data.data);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  const fetchForms = async (pageId, userToken) => {
    try {
      const response = await axios.get("https://leadbankapi.gensparc.com/api/facebook/forms", {
        params: {
          pageId: pageId,
          token: userToken,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
  
      setForms(response.data.data || []);
      console.log("Fetched Forms:", response.data);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  const subscribePageToWebhook = async (pageId, pageAccessToken) => {
    try {
      const jwtToken = localStorage.getItem("token");
      const response = await axios.post(
        "https://leadbankapi.gensparc.com/api/facebook/subscribe-page",
        { pageId, pageAccessToken },
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      console.log("✅ Webhook subscription successful:", response.data);
    } catch (error) {
      console.error("❌ Webhook subscription failed:", error.response?.data || error.message);
    }
  };

  useEffect(() => {
    if (selectedPage && token) {
      fetchForms(selectedPage, token);
  
      const selectedPageObj = pages.find(p => p.id === selectedPage);
      const pageAccessToken = selectedPageObj?.access_token;
  
      if (pageAccessToken) {
        subscribePageToWebhook(selectedPage, pageAccessToken); // ✅ Correct token now
      } else {
        console.error("Page Access Token not found for the selected page.");
      }
    }
  }, [selectedPage, token, pages]);
  
  const debouncedSaveUserSelection = React.useRef(
    debounce((data) => {
      saveUserSelection(data);
    }, 500)
  ).current;

  useEffect(() => {
    if (selectedPage && selectedForm && name && token && leads) {
      debouncedSaveUserSelection({ pageId: selectedPage, formId: selectedForm, token, name, leads });
    }
  }, [selectedPage, selectedForm, name, token, leads]);

  const saveUserSelection = async ({ pageId, formId, token, name, leads }) => {
    try {
      const Authtoken = localStorage.getItem("token");

      const selectedPageObj = pages.find((p) => p.id === pageId);
      const selectedFormObj = forms.find((f) => f.id === formId);

      const pageName = selectedPageObj?.name || "";
      const formName = selectedFormObj?.name || "";

      console.log("Saving selection with names:", {
        pageName,
        formName,
        name,
        token,
        leads,
      });

    } catch (error) {
      console.error("Error saving selection:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const Authtoken = localStorage.getItem("token");

      const response = await axios.get("https://leadbankapi.gensparc.com/api/facebook/leads", {
        headers: { Authorization: `Bearer ${Authtoken}` },
        params: { formId: selectedForm, token },
      });

      const fetchedLeads = response.data.data;
      setLeads(fetchedLeads);

      const selectedPageObj = pages.find((p) => p.id === selectedPage);
      const selectedFormObj = forms.find((f) => f.id === selectedForm);

      const pageName = selectedPageObj?.name || "";
      const formName = selectedFormObj?.name || "";

      await axios.post(
        "https://leadbankapi.gensparc.com/api/facebook/save-selection",
        { pageName, formName, pageId: selectedPage, formId: selectedForm, name, token, leads: fetchedLeads },
        { headers: { Authorization: `Bearer ${Authtoken}` } }
      );
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  return (
    <div className="leads-container">
      <h1>Facebook Leads</h1>

      {!token ? (
        <button className="leads-button" onClick={openFacebookLoginPopup}>
          Get Leads from Facebook
        </button>
      ) : (
        <div>
        <input
  type="text"
  placeholder="Enter Campaign Name"
  value={name}
  onChange={(e) => {
    const newName = e.target.value;
    setname(newName);
    debouncedSaveUserSelection({
      pageId: selectedPage,
      formId: selectedForm,
      token,
      name: newName,
      leads,
    }); // ✅ Pass proper object
  }}
  className="campaign-input"
/>


          <select onChange={(e) => setSelectedPage(e.target.value)} value={selectedPage}>
            <option value="">Select Page</option>
            {pages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.name}
              </option>
            ))}
          </select>

          <select onChange={(e) => setSelectedForm(e.target.value)} value={selectedForm}>
            <option value="">Select Form</option>
            {forms.map((form) => (
              <option key={form.id} value={form.id}>
                {form.name}
              </option>
            ))}
          </select>

          <button className="leads-button" onClick={fetchLeads} disabled={!selectedForm}>
            Fetch Leads
          </button>

          <ul className="leads-list">
            {leads.map((lead, index) => (
              <li key={index} className="lead-item">{JSON.stringify(lead)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Facebook;
